import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha } from "@mui/material/styles";
import ScienceIcon from "@mui/icons-material/Science";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
// Removed unused icons to simplify the final screen
import { useDashboard } from "../../../contexts/DashboardContext";
import { useAuth } from "../../../hooks/useAuth";
import { useChatContext } from "../../agent/contexts/ChatContext";
import CowTeatPicker from "./CowTeatPicker";
import { MastitisTestService, MastitisTestData } from "../services/MastitisTestService";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import infoMastitis from "../../../../info-mastitis.json";
import { getTreatmentByOrganism, AINES_RECOMENDADOS } from "../services/mastitisTreatment";

interface MastitisTestStep3Props {
  initialData?: any;
}

const MastitisTestStep3: React.FC<MastitisTestStep3Props> = ({
  initialData,
}) => {
  const { updateContent, restorePreviousContent } = useDashboard();
  const { authState } = useAuth();
  const { selectedOrganization, activeSessionId, sessions } = useChatContext();
  
  const selectionsPrimary = initialData?.mastitis?.selectionsPrimary || {};
  const selectionsSecondary =
    initialData?.mastitis?.selectionsSecondary || null;
  const clinical = initialData?.mastitis?.clinical || null;
  const teats: string[] = initialData?.mastitis?.teats || [];
  const [finalNotes, setFinalNotes] = useState<string>(
    initialData?.mastitis?.finalNotes || ""
  );
  const plateImageFile = initialData?.mastitis?.plateImageFile;
  
  // Estados para o envio
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const plateImageUrl = useMemo(() => {
    try {
      return plateImageFile ? URL.createObjectURL(plateImageFile) : null;
    } catch {
      return null;
    }
  }, [plateImageFile]);

  // Estados de UI para animação e telas de resultado
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [sendOutcome, setSendOutcome] = useState<'idle' | 'success' | 'error'>('idle');
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [resultType, setResultType] = useState<'positive' | 'negative' | 'error' | 'idle'>('idle');
  const [lastSentData, setLastSentData] = useState<any | null>(null);

  const results = (infoMastitis as any)?.ResultsMastitis || [];

  const CATEGORY_FLOW: { key: string; title: string }[] = [
    {
      key: "ImagemCromogenicoStaphylococcus",
      title: "Cromogênico Staphylococcus (vermelho)",
    },
    {
      key: "ImagemCromogenicoGeral",
      title: "Cromogênico Geral (transparente)",
    },
    {
      key: "ImagemCromogenicoStreptococcus",
      title: "Cromogênico Streptococcus (branco)",
    },
  ];

  const matchFor = (sel?: any) => {
    if (!sel) return null;
    const staph = sel?.staphylococcus;
    const geral = sel?.geral;
    const strep = sel?.streptococcus;
    const pairs: Array<{ key: string; val?: string | null }> = [
      { key: "ImagemCromogenicoStaphylococcus", val: staph },
      { key: "ImagemCromogenicoGeral", val: geral },
      { key: "ImagemCromogenicoStreptococcus", val: strep },
    ];
    const active = pairs.filter((p) => Boolean(p.val));
    if (active.length === 0) return null;

    let best: any = null;
    let bestScore = -1;
    for (const item of results) {
      let score = 0;
      for (const p of active) {
        if ((item as any)?.[p.key] === p.val) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    }
    return best;
  };

  const matchPrimary = useMemo(
    () => matchFor(selectionsPrimary),
    [results, selectionsPrimary]
  );
  const matchSecondary = useMemo(
    () => matchFor(selectionsSecondary),
    [results, selectionsSecondary]
  );

  const treatmentPlan = useMemo(
    () => getTreatmentByOrganism(matchPrimary?.Microrganismo || ''),
    [matchPrimary]
  );

  useEffect(() => {
    if (matchPrimary) {
      const organism = matchPrimary?.Microrganismo || 'Indefinido';
      const logPayload = {
        diagnostico: organism,
        tratamento: {
          situacao: treatmentPlan?.situacao,
          condutaPrincipal: treatmentPlan?.condutaPrincipal,
          suporte: treatmentPlan?.suporte,
          antibioticos: treatmentPlan?.antibioticos,
          aines: treatmentPlan?.aines,
        },
      };
      
    }
  }, [matchPrimary, treatmentPlan]);
  const handleBack = () => {
    updateContent("mastitisTestStep2", initialData);
  };

  const handleCancel = () => {
    updateContent("welcome");
  };

  const handleNotesBlur = () => {
    const payload = {
      ...(initialData || {}),
      mastitis: {
        ...(initialData?.mastitis || {}),
        finalNotes: finalNotes?.trim() || null,
      },
    };
    updateContent("mastitisTestStep3", payload);
  };

  const deriveResultType = (data: any): 'positive' | 'negative' => {
    const examResult = data?.examResult || null;
    const sp = data?.selectionsPrimary || {};
    const ss = data?.selectionsSecondary || {};
    const hasOrganism = Boolean(sp?.staphylococcus || sp?.streptococcus || sp?.geral || ss?.staphylococcus || ss?.streptococcus || ss?.geral);
    if (examResult && typeof examResult === 'string') {
      return 'positive';
    }
    return hasOrganism ? 'positive' : 'negative';
  };

  const handleSubmitTest = async () => {
    // Seguindo o padrão do ChatContext para obter dados de usuário e organização
    const session = sessions.find((s) => s.session_id === activeSessionId);
    const activeOrganization = selectedOrganization;
    const ownerId = (activeOrganization as any)?.owner_id || authState.profileId || authState.user?.id;
    const businessUnitId = session?.organization_id || (activeOrganization as any)?._key || activeOrganization?.id;

    if (!ownerId || !businessUnitId || !activeSessionId) {
      setSubmitError("Dados de usuário ou organização não encontrados");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setShowAnalysis(true);
    setResultModalOpen(false);
    setSendOutcome('idle');
    setResultMessage(null);
    setResultType('idle');

    try {
      const testData: MastitisTestData = {
        animalId: initialData?.mastitis?.animalId,
        teats,
        plateImageFile,
        collectionDate: initialData?.mastitis?.collectionDate,
        selectionsPrimary,
        selectionsSecondary,
        clinical,
        finalNotes: finalNotes?.trim() || undefined,
      };

      setLastSentData(testData);

      const sendPromise = MastitisTestService.sendMastitisTest(
        testData,
        ownerId,
        businessUnitId,
        activeSessionId
      );
      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
      const [result] = await Promise.all([
        sendPromise,
        sleep(5000), // animação ~5s
      ]);

      if (result.success) {
        setSubmitSuccess(true);
        setSendOutcome('success');
        const rType = deriveResultType(testData);
        setResultType(rType);
        setResultMessage(rType === 'positive' ? 'Indícios de mastite.' : 'Sem evidências relevantes de mastite.');
      } else {
        const errMsg = result.error || "Erro ao enviar teste";
        setSubmitError(errMsg);
        setSendOutcome('error');
        setResultType('error');
        setResultMessage(errMsg);
      }
    } catch (error) {
      console.error("Erro ao enviar teste:", error);
      const errMsg = "Erro inesperado ao enviar teste";
      setSubmitError(errMsg);
      setSendOutcome('error');
      setResultType('error');
      setResultMessage(errMsg);
    } finally {
      setIsSubmitting(false);
      setShowAnalysis(false);
      setResultModalOpen(true);
    }
  };

  const handleRetry = async () => {
    if (!lastSentData) return;
    setResultModalOpen(false);
    setSendOutcome('idle');
    setResultType('idle');
    setResultMessage(null);
    await handleSubmitTest();
  };

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
    >
      {/* Header com ações discretas */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Análise de Mastite
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={handleCancel}
          sx={{ color: "text.secondary", textTransform: "none" }}
        >
          Cancelar
        </Button>
      </Box>

      {/* Tela Final: somente resumo detalhado com diagnóstico final */}
      <Box sx={{ height: "100%" }}>
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxWidth: 960,
            mx: "auto",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
          })}
        >
          {showAnalysis ? (
            // Estado: animação de análise (inline, sem modal)
            (() => {
              const float = keyframes({
                '0%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-10px) rotate(-3deg)' },
                '100%': { transform: 'translateY(0)' },
              });
              const pulse = keyframes({
                '0%': { opacity: 0.7 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.7 },
              });
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 6 }}>
                  <Box sx={{ position: 'relative', mb: 1 }}>
                    <ScienceIcon sx={{ fontSize: 64, color: '#7C3AED', animation: `${float} 2.2s ease-in-out infinite` }} />
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <Box sx={{ width: 96, height: 96, borderRadius: '50%', border: '2px dashed #C4B5FD', animation: `${pulse} 1.6s ease-in-out infinite` }} />
                    </Box>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Análise Laboratorial
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Processando amostra e preparando registro...
                  </Typography>
                </Box>
              );
            })()
          ) : resultModalOpen ? (
            // Estado: resultado (inline, sem modal)
            <Box>
              {sendOutcome === 'success' && resultType === 'positive' && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  minHeight: '400px',
                  textAlign: 'center'
                }}>
                  {/* Ícone de sucesso */}
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: 80, 
                      color: 'success.main', 
                      mb: 3 
                    }} 
                  />
                  
              {/* Mensagem de sucesso */}
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                Registro realizado com sucesso!
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
                Animal: {initialData?.mastitis?.animalId || 'N/A'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                O teste de mastite foi realizado e vinculado ao passaporte do animal {initialData?.mastitis?.animalId || 'N/A'}.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center', maxWidth: 560 }}>
                Esta análise é gerada por IA com base nas imagens e informações selecionadas. Não constitui diagnóstico e não substitui avaliação clínica. Consulte sempre um(a) médico(a) veterinário(a).
              </Typography>
                  
                  {/* Botões de ação */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => updateContent("mastitisIntro")}
                      sx={{
                        minWidth: 200,
                        py: 1.5,
                        fontSize: '1.1rem',
                      }}
                    >
                      Fazer novo teste
                    </Button>
                    <Button variant="outlined" size="large" onClick={handleCancel} sx={{ minWidth: 160, py: 1.5 }}>
                      Sair
                    </Button>
                  </Box>
                </Box>
              )}

              {sendOutcome === 'success' && resultType === 'negative' && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  minHeight: '400px',
                  textAlign: 'center'
                }}>
                  {/* Ícone de sucesso */}
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: 80, 
                      color: 'success.main', 
                      mb: 3 
                    }} 
                  />
                  
              {/* Mensagem de sucesso */}
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                Registro realizado com sucesso!
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                Animal: {initialData?.mastitis?.animalId || 'N/A'}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Sem evidências relevantes de mastite
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                O teste de mastite foi realizado e vinculado ao passaporte do animal {initialData?.mastitis?.animalId || 'N/A'}.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center', maxWidth: 560 }}>
                Esta análise é gerada por IA com base nas imagens e informações selecionadas. Não constitui diagnóstico e não substitui avaliação clínica. Consulte sempre um(a) médico(a) veterinário(a).
              </Typography>
                  
                  {/* Botões de ação */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => updateContent("mastitisIntro")}
                      sx={{
                        minWidth: 200,
                        py: 1.5,
                        fontSize: '1.1rem',
                      }}
                    >
                      Fazer novo teste
                    </Button>
                    <Button variant="outlined" size="large" onClick={handleCancel} sx={{ minWidth: 160, py: 1.5 }}>
                      Sair
                    </Button>
                  </Box>
                </Box>
              )}

              {sendOutcome === 'error' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Algo deu errado</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 420 }}>
                    {submitError || "Não foi possível registrar as informações no laboratório agora. Tente novamente em breve."}
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleRetry}>Tentar novamente</Button>
                    <Button variant="outlined" onClick={() => setResultModalOpen(false)}>Fechar</Button>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            // Tela de sucesso como padrão
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {/* Cabeçalho bonito com destaque da análise */}
              <Paper elevation={0} sx={(theme) => ({
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                background: `linear-gradient(180deg, ${alpha(theme.palette.success.light, 0.18)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
              })}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Indícios de mastite</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Análise sugerida de microrganismo: <b>{matchPrimary?.Microrganismo || '—'}</b>
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                  Esta análise é gerada por IA com base nas imagens e informações selecionadas. Não constitui diagnóstico e não substitui avaliação clínica.
                </Typography>
              </Paper>

              {/* Tratamento recomendado e evidências do exame */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Paper elevation={0} sx={(theme) => ({
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.default,
                  })}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Conduta sugerida</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Consulte o veterinário e siga o protocolo da fazenda.
                      Priorize terapia baseada em <b>antibiograma</b> quando disponível. Considere
                      anti-inflamatório e suporte ao manejo conforme o quadro clínico.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <b>Situação:</b> {treatmentPlan?.situacao || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <b>Conduta principal:</b> {treatmentPlan?.condutaPrincipal || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <b>Tratamento de suporte:</b> {treatmentPlan?.suporte || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <b>Antibióticos recomendados:</b> {treatmentPlan?.antibioticos || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <b>AINEs recomendados:</b> {(treatmentPlan?.aines || AINES_RECOMENDADOS).join(', ')}
                    </Typography>

                  </Paper>
                </Box>
                <Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Paper elevation={0} sx={(theme) => ({
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        backgroundColor: alpha(theme.palette.warning.light, 0.1),
                        height: '100%',
                      })}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Quadro clínico</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Leite: {clinical?.milkCondition || '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Úbere: {(clinical?.udderConditions || []).length > 0 ? (clinical?.udderConditions || []).join(', ') : '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                          Severidade: {clinical?.severity
                            ? clinical?.severity === 'grave' ? 'Grave'
                              : clinical?.severity === 'medio' ? 'Médio'
                              : 'Leve'
                            : '—'}
                        </Typography>
                        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {clinical?.severity === 'grave' && (
                            <Chip label="Atendimento imediato" color="error" />
                          )}
                          {clinical?.severity === 'medio' && (
                            <Chip label="Avaliação veterinária" color="warning" />
                          )}
                          {clinical?.severity === 'leve' && (
                            <Chip label="Higienização e observação" color="success" />
                          )}
                        </Box>
                      </Paper>
                    </Box>
                    <Box>
                      <Paper
                        elevation={0}
                        sx={(theme) => ({
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.default,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                        })}
                      >
                        <Box sx={{ width: { xs: 180, md: 220 } }}>
                          <CowTeatPicker
                            backgroundSrc={'/vaca-pespective.svg'}
                            value={teats as any}
                            readOnly
                            compact
                            teatPercent={16}
                            fontSizeOverride={12}
                          />
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Campo de observações */}
              <Paper elevation={0} sx={(theme) => ({
                p: 2,
                mt: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
              })}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Observações</Typography>
                <TextField
                  placeholder="Adicione comentários sobre o teste, manejo ou contexto."
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  value={finalNotes}
                  onChange={(e) => setFinalNotes(e.target.value)}
                  onBlur={handleNotesBlur}
                  variant="filled"
                  InputProps={{ disableUnderline: true }}
                  sx={(theme) => ({
                    "& .MuiInputBase-root": {
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 2,
                      boxShadow: "none",
                    },
                  })}
                />
              </Paper>

              {/* Ações */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ textTransform: "none" }}
                >
                  Refazer seleção
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitTest}
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    minWidth: 180,
                  }}
                >
                  {isSubmitting ? "Enviando..." : "Registrar"}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Removidos overlays/modais: tudo renderizado inline no painel direito */}
    </Box>
  );
};

export default MastitisTestStep3;
